import { useMutation } from '@ts-gql/apollo';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';
import React from 'react';

import { Maybe } from '../../__generated__/ts-gql/@schema';

import { GET_TASK_QUERY, GetTaskQueryResult } from './useGetTask';
import { SEARCH_TASKS_QUERY, SearchTasksQueryResult } from './useSearchTasks';

export const SEARCH_TASKS_ARGS_KEY = 'workflow_plus_searchTasksArgs';

const mutation = gql`
  mutation updateTask($input: TaskUpdateInput!) {
    updateTask(input: $input) {
      correlationId
      validationMessage {
        field
        message
      }
      messagesSuccessfullyProcessed
    }
  }
` as import('../../__generated__/ts-gql/updateTask').type;

export type UpdateTaskVariables = OperationVariables<typeof mutation>['input'];
export type UpdateTaskResult = OperationData<typeof mutation>;

export const useUpdateTask = () => {
  const tuple = useMutation(mutation);

  const searchTasksArgs = JSON.parse(
    window.localStorage.getItem(SEARCH_TASKS_ARGS_KEY) || ''
  );

  const [_mutate, result] = tuple;

  const mutate = React.useCallback<typeof _mutate>(
    (options) =>
      _mutate({
        context: { service: 'core' },
        update(cache) {
          const getTaskResult: GetTaskQueryResult | null = cache.readQuery({
            query: GET_TASK_QUERY,
            variables: {
              cloudId: options.variables?.input.cloudId,
            },
          });

          if (!getTaskResult?.getTask) return;

          const updatedTaskResult = {
            ...getTaskResult?.getTask,
            name: options.variables?.input.name,
            description: options.variables.input.description || '',
            assignedToUserId: options.variables.input
              .assignedToUserId as Maybe<string>,
            estimateMinutes: options.variables.input
              .estimateMinutes as Maybe<number>,
            dueDate: options.variables.input.dueDate,
          };

          const getSearchTasksResult: SearchTasksQueryResult | null =
            cache.readQuery({
              query: SEARCH_TASKS_QUERY,
              variables: searchTasksArgs,
            });

          cache.writeQuery({
            query: GET_TASK_QUERY,
            variables: {
              cloudId: options.variables?.input.cloudId,
            },
            data: {
              getTask: updatedTaskResult,
            },
          });

          if (getSearchTasksResult) {
            const filteredResults =
              getSearchTasksResult?.searchTasks?.filter(
                (task) => task.cloudId !== options.variables?.input.cloudId
              ) || [];

            cache.writeQuery({
              query: SEARCH_TASKS_QUERY,
              variables: searchTasksArgs,
              data: {
                searchTasks: [updatedTaskResult, ...filteredResults],
              },
            });
          }
        },
        optimisticResponse: {
          updateTask: {
            correlationId: '',
            messagesSuccessfullyProcessed: true,
            validationMessage: null,
          },
        },
        ...options,
      }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_mutate]
  );

  return React.useMemo<typeof tuple>(() => [mutate, result], [mutate, result]);
};
